// The sign-in page at `/` (02-DESIGN/03-console/01-the-console.md § signing
// in): the mark and the wordmark, and one card — *Sign in*, one primary button
// named for the platform provider, *or*, and *Your workspace*, a field for its
// name or the person's work email that resolves to `/<slug>`. The page is
// also the callback: a return from the provider lands here with a code and a
// state and shows *Completing sign-in*, nothing to click. Signed in under the platform provider with no workspace named, it
// is *Choose a workspace*. Signed out on an install that allows sign-up, the
// card closes the front page (03-the-signed-out-site.md; decision 0072),
// where its lead says the first sign-in creates a workspace and its primary
// reads *Get started*.

import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";

import { safeReturnTo, slugHint, tenantPath } from "../../address/address.ts";
import { PLATFORM_NAME } from "../../platform/names.ts";
import { usePage } from "../../platform/session.tsx";
import { complete } from "../../signin/flow.ts";
import { saveProof } from "../../signin/tokens.ts";
import { Button, describe, Field, Notice, TextInput } from "../../ui/components.tsx";
import { frontOf } from "../site/front.ts";
import { SitePage } from "../site/SitePage.tsx";
import { CARD_LEAD, GET_STARTED } from "../site/words.ts";
import { ChooseWorkspace } from "./ChooseWorkspace.tsx";
import { Entry } from "./Entry.tsx";
import { usePlatformSignIn } from "./platformSignIn.ts";

export function SignInPage() {
  // The router's location, not window.location: once the return completes
  // and navigates away from ?code=, this page must render again and leave
  // the callback behind.
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const returning = params.has("code") || params.has("error");
  if (returning) {
    return <Returning url={new URL(location.pathname + location.search, window.location.origin)} />;
  }
  return <Front />;
}

function Returning({ url }: { url: URL }) {
  const { origin, setSession } = usePage();
  const navigate = useNavigate();
  const [err, setErr] = useState<unknown>(undefined);
  useEffect(() => {
    let live = true;
    complete(origin, url).then(
      ({ session, returnTo, probe }) => {
        if (!live) {
          return;
        }
        // A sign-in that proves a switch is handed to the Sign-in tab, not
        // connected with: the person stays signed in as before until the
        // switch succeeds (03-signing-in.md § switching a workspace's provider).
        if (probe !== undefined) {
          saveProof({ probe, session });
          void navigate(safeReturnTo(returnTo), { replace: true });
          return;
        }
        setSession(session);
        void navigate(safeReturnTo(returnTo), { replace: true });
      },
      (e: unknown) => {
        if (live) {
          setErr(e);
        }
      },
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the return is completed once, for the URL the page loaded with
  }, []);
  if (err !== undefined) {
    return (
      <Entry>
        <div className="entry-card">
          <h1>Sign-in did not complete</h1>
          <p className="lead">{describe(err)}</p>
          <Button
            kind="primary"
            size="lg"
            block
            onClick={() => {
              void navigate("/", { replace: true });
            }}
          >
            Try again
          </Button>
        </div>
      </Entry>
    );
  }
  return (
    <Entry>
      <p className="faint" aria-busy="true">
        Completing sign-in…
      </p>
    </Entry>
  );
}

function Front() {
  const { platform, session } = usePage();

  if (session !== undefined && session.slug !== "") {
    return <Navigate to={tenantPath(session.slug)} replace />;
  }
  if (session !== undefined) {
    return <ChooseWorkspace />;
  }
  // Where the install lets a stranger sign up, `/` signed out is the front
  // page with this card closing it (03-the-signed-out-site.md; decisions
  // 0054 and 0071); where it does not, the card alone, as before.
  if (frontOf(platform) === "site") {
    return <SitePage card={<SignInCard inPage />} />;
  }
  return (
    <Entry>
      <SignInCard />
    </Entry>
  );
}

/**
 * SignInCard is the sign-in page's one card: *Sign in*, the one primary
 * button, *or*, and *Your workspace*. The front page carries the same
 * component, with two words of its own: the lead says the first sign-in
 * creates a workspace, and the primary reads *Get started*.
 */
export function SignInCard({ inPage = false }: { inPage?: boolean }) {
  // On the site the page's headline is the one h1; the card is a section of it.
  const Heading = inPage ? "h2" : "h1";
  const navigate = useNavigate();
  const [typed, setTyped] = useState("");
  const { choice, choiceErr, busy, err, setErr, signIn } = usePlatformSignIn();
  const { platform } = usePage();
  const provider = PLATFORM_NAME;
  // On the site the card says the first sign-in creates a workspace only where
  // the install lets it (decision 0074); where sign-up is off, the card is the
  // sign-in page's own in every word, for a person who was given a workspace.
  const creates = inPage && platform.sign_up;

  const go = () => {
    const slug = slugHint(typed);
    if (slug === null) {
      setErr("Type the workspace's name, or your work email.");
      return;
    }
    void navigate(tenantPath(slug));
  };

  return (
    <div className="entry-card" id="sign-in">
      <Heading>Sign in</Heading>
      <p className="lead">{creates ? CARD_LEAD : "Continue with the account you already have."}</p>
      {err !== undefined && <Notice kind="bad">{err}</Notice>}
      {choiceErr !== undefined && (
        <Notice kind="bad" title={`${provider} cannot sign you in here`}>
          {describe(choiceErr)}
        </Notice>
      )}
      <Button kind="primary" size="lg" block onClick={signIn} disabled={choice === undefined || busy}>
        {creates ? GET_STARTED : `Continue with ${provider}`}
      </Button>
      <div className="or">or</div>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          go();
        }}
      >
        <Field label="Your workspace" hint="For a workspace that signs in with its own provider.">
          <TextInput
            value={typed}
            onChange={(v) => {
              setTyped(v);
              setErr(undefined);
            }}
            placeholder="Its name, or your work email"
          />
        </Field>
        <Button type="submit" size="lg" block disabled={typed.trim() === ""}>
          Continue
        </Button>
      </form>
    </div>
  );
}
